import numeral from "numeral";
import { NO_VALUE_OPERATORS } from "~/helpers/advanced_filters_helpers";
import dayjs from "~/lib/dayjs";

/**
 * Format a numeric value as currency (e.g., $1,234.56)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted currency string or '-' if empty
 */
export const formatCurrency = (value) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return "-";
  }
  if (!Number.isFinite(Number(value))) {
    return "-";
  }
  return numeral(Number(value)).format("$0,0[.]00");
};

/**
 * Format a date string as a short localized date (e.g., "Jan 15, 2025")
 * Uses dayjs for consistent timezone handling across all browsers.
 * @param {string} dateStr - The date string to format
 * @returns {string} Formatted date or '-' if empty
 */
export const formatDate = (dateStr) => {
  if (!dateStr) {
    return "-";
  }
  const date = dayjs(dateStr);
  if (!date.isValid()) {
    return "-";
  }
  return date.format("MMM D, YYYY");
};

/**
 * Format a filter for display as human-readable text
 * @param {Object} filter - The filter object
 * @param {Function} getFilterCategoriesForPage - Function to get filter categories
 * @param {string} pageType - The page type for looking up categories
 * @returns {string|null} Formatted filter text or null
 */
export const formatFilterDisplay = (
  filter,
  getFilterCategoriesForPage,
  pageType,
) => {
  const { category, property, operator, appliedValues, value, valueEnd } =
    filter;

  if (!property && !category) {
    return null;
  }

  const isNoValueOp = NO_VALUE_OPERATORS.includes(operator);

  // BK-2907: shape B filters carry chips in appliedValues, shape A filters
  // carry scalar value/valueEnd. Read both paths so numeric, date, date_range,
  // date_relative, boolean, and `date + between` render correctly.
  let valueDisplay = "";
  if (appliedValues && appliedValues.length > 0) {
    valueDisplay = appliedValues.map((v) => v.label || v.value).join(", ");
  } else if (value !== "" && value != null) {
    if (valueEnd !== "" && valueEnd != null) {
      valueDisplay = `${value} to ${valueEnd}`;
    } else {
      valueDisplay = String(value);
    }
  }

  // No-value operators (is_empty, is_not_empty, today, etc.) don't need a value
  if (!valueDisplay && !isNoValueOp) {
    return null;
  }

  let propertyLabel = property;
  if (category && property) {
    const filterCategories = getFilterCategoriesForPage(pageType);
    if (!filterCategories) {
      return `${propertyLabel} ${operator || "equals"}${valueDisplay ? ` ${valueDisplay}` : ""}`;
    }
    const categoryConfig = filterCategories[category];
    if (categoryConfig && categoryConfig.properties) {
      const propertyConfig = categoryConfig.properties.find(
        (p) => p.value === property,
      );
      if (propertyConfig) {
        propertyLabel = propertyConfig.label;
      }
    }
  }

  const operatorDisplay = operator || "equals";
  return valueDisplay
    ? `${propertyLabel} ${operatorDisplay} ${valueDisplay}`
    : `${propertyLabel} ${operatorDisplay}`;
};

// Single cached Intl instance — faster than constructing per call.
const usdCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatPrice = (price, { emptyValue = "N/A" } = {}) => {
  // Preserve legacy behaviour from the `!price` guard: numeric zero renders as
  // emptyValue. Callers that want "$0" should pass `emptyValue: "$0"`.
  if (price == null || price === "" || price === 0) {
    return emptyValue;
  }
  const num = Number(price);
  if (!Number.isFinite(num)) {
    return emptyValue;
  }
  return usdCurrencyFormatter.format(num);
};

/**
 * Format a date with a custom dayjs format string. Returns emptyValue on null,
 * empty, or invalid input. Replaces ad-hoc per-feature date helpers.
 * @param {string} value
 * @param {string} format - dayjs format string (default "MMM D, YYYY")
 * @param {string} emptyValue - returned when value is missing or invalid
 */
export const formatDateTime = (
  value,
  format = "MMM D, YYYY",
  emptyValue = "-",
) => {
  if (!value) {
    return emptyValue;
  }
  const d = dayjs(value);
  if (!d.isValid()) {
    return emptyValue;
  }
  return d.format(format);
};

export const formatUSDate = (date) => {
  if (!date) {
    return "N/A";
  }
  const parsedDate = new Date(date);
  return parsedDate.toLocaleDateString("en-US", { timeZone: "UTC" });
};

export const formatValue = (value) => value || "N/A";

/**
 * Formats a number with comma separators.
 *
 * @param {number} num - The number to format.
 * @returns {string} The formatted number with commas, e.g., 10,000.
 */
export const formatNumber = (num) => {
  if (typeof num !== "number") {
    return 0;
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};
